import {
  EPriceType,
  EMAIL_SCHEMA,
  IBuildOnDemandOrderData,
  IBuildOnDemandOrderConfirmationRequestData,
  IOnDemandOrderConfirmationItem,
  IOnDemandOrderConfirmationRequest,
  IOnDemandOrderData,
} from '@web/dtos';

const VIENNA_TIME_ZONE = 'Europe/Vienna';

export function buildOnDemandOrderConfirmationRequest(
  data: IBuildOnDemandOrderConfirmationRequestData
): IOnDemandOrderConfirmationRequest {
  return {
    ...buildOnDemandOrderData(data),
    transactionId: data.transactionId,
  };
}

export function buildOnDemandOrderData(
  data: IBuildOnDemandOrderData
): IOnDemandOrderData {
  const {
    customerReference,
    customer,
    reservation,
    booking,
    trip,
    connection,
    operatorMapping,
    onDemandLeg,
    selections,
    passengerData,
    extrasPerPassenger,
    totalAmount,
    paymentMethod,
    onDemandProduct,
    comfortProduct,
    technicalCancellationReason,
    labels,
    getPrice,
    getPublicTransportTicketLabel,
  } = data;

  if (!customer || !reservation || !booking || !trip || !connection) {
    throw new Error('On-demand order confirmation data is incomplete.');
  }

  const regularLegs = trip.legs.filter(
    leg => leg.type === 'JNY' && leg.operator.id !== onDemandLeg?.operator.id
  );
  const regularOrigin = regularLegs[0]?.origin.name ?? trip.origin.name;
  const regularDestination =
    regularLegs[regularLegs.length - 1]?.destination.name ??
    trip.destination.name;
  const passengerNames = selections.map((_, index) =>
    passengerData[index]
      ? `${passengerData[index].firstName} ${passengerData[index].lastName}`
      : `${customer.firstName} ${customer.lastName}`
  );
  const items: IOnDemandOrderConfirmationItem[] = selections.flatMap(
    (selection, index) => {
      const passenger = `${passengerNames[index]} (${selection.fahrgasttyp})`;
      if (selection.useExistingTicket) {
        return [
          {
            type: EPriceType.COMFORT,
            product: comfortProduct,
            passenger,
            origin: onDemandLeg?.origin.name ?? trip.origin.name,
            destination: onDemandLeg?.destination.name ?? trip.destination.name,
            vatRate: 0,
            grossPrice: getPrice(EPriceType.COMFORT, selection.fahrgasttyp),
          },
        ];
      }

      const passengerItems: IOnDemandOrderConfirmationItem[] = [
        {
          type: EPriceType.ON_DEMAND,
          product: onDemandProduct,
          passenger,
          origin: onDemandLeg?.origin.name ?? trip.origin.name,
          destination: onDemandLeg?.destination.name ?? trip.destination.name,
          vatRate: 0,
          grossPrice: getPrice(EPriceType.ON_DEMAND, selection.fahrgasttyp),
        },
      ];
      const publicTransportPrice = getPrice(
        EPriceType.PUBLIC_TRANSPORT,
        selection.fahrgasttyp
      );
      if (publicTransportPrice > 0) {
        passengerItems.push({
          type: EPriceType.PUBLIC_TRANSPORT,
          product: getPublicTransportTicketLabel(selection.fahrgasttyp),
          passenger,
          origin: regularOrigin,
          destination: regularDestination,
          vatRate: 0,
          grossPrice: publicTransportPrice,
        });
      }
      return passengerItems;
    }
  );

  return {
    bookingReference: reservation.bookingRef,
    systemRef: booking.systemRef,
    operatorUrl: operatorMapping?.url ?? '',
    customerReference,
    customer: {
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      phone: customer.phone,
    },
    totalAmount,
    technicalCancellationReason,
    paymentMethod,
    journey: {
      date: trip.origin.date,
      origin: trip.origin.name,
      destination: trip.destination.name,
      departure: booking.confirmedDeparture ?? connection.departureTimetabled,
      arrival: booking.confirmedArrival ?? connection.arrivalTimetabled,
      operator: operatorMapping?.name,
      changeableUntil: booking.changeableUntil,
      cancellableUntil: booking.cancellableUntil,
    },
    passengers: selections.map((selection, index) => ({
      name: passengerNames[index],
      passengerType: selection.fahrgasttyp,
      existingPublicTransportTicket: selection.existingTicketType ?? undefined,
      extras: extrasPerPassenger[index] ?? [],
    })),
    publicTransportTickets: [
      ...selections
        .map(selection => selection.existingTicketType)
        .filter((ticket): ticket is string => Boolean(ticket)),
      ...items
        .filter(item => item.type === EPriceType.PUBLIC_TRANSPORT)
        .map(item => item.product),
    ],
    items,
    labels,
  };
}

export function validateOnDemandOrderConfirmationRequest(
  request: IOnDemandOrderConfirmationRequest
): void {
  const serializedRequest = JSON.stringify(request);
  const transactionIdPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (
    !request?.transactionId?.trim() ||
    !transactionIdPattern.test(request.transactionId) ||
    !request.bookingReference?.trim() ||
    !request.systemRef?.trim() ||
    !request.operatorUrl?.trim() ||
    !request.customer?.email?.trim() ||
    !EMAIL_SCHEMA.safeParse(request.customer.email).success ||
    !request.customer.firstName?.trim() ||
    !request.customer.lastName?.trim() ||
    !request.customerReference?.trim() ||
    !Number.isFinite(request.totalAmount) ||
    request.totalAmount <= 0 ||
    !request.journey?.origin?.trim() ||
    !request.journey.destination?.trim() ||
    !Array.isArray(request.passengers) ||
    request.passengers.length === 0 ||
    !Array.isArray(request.items) ||
    request.items.length === 0 ||
    request.items.some(
      item =>
        !Object.values(EPriceType).includes(item.type) ||
        !item.product?.trim() ||
        !item.passenger?.trim() ||
        !Number.isFinite(item.grossPrice) ||
        item.grossPrice < 0
    ) ||
    Object.values(request.labels ?? {}).some(label => !label?.trim()) ||
    Object.keys(request.labels ?? {}).length !== 9 ||
    serializedRequest.length > 50_000
  ) {
    throw new Error('On-demand order confirmation data is incomplete.');
  }
}

export function buildOnDemandOrderConfirmationVariables(
  request: IOnDemandOrderConfirmationRequest,
  now: Date = new Date()
): Record<string, string> {
  const passengerSummary = request.passengers
    .map(passenger => `${passenger.name} (${passenger.passengerType})`)
    .join(', ');
  const existingTickets = request.passengers
    .map(passenger => passenger.existingPublicTransportTicket)
    .filter((ticket): ticket is string => Boolean(ticket));
  const extras = request.passengers.flatMap(passenger => passenger.extras);
  const journeyDate = request.journey.date
    ? formatDate(request.journey.date)
    : '';
  const cancellationTimestamp = request.journey.cancellableUntil
    ? new Date(request.journey.cancellableUntil).getTime()
    : Number.NaN;
  const isCancellationDeadlineExpired =
    Number.isFinite(cancellationTimestamp) &&
    cancellationTimestamp <= now.getTime();
  const cancellationText = request.journey.cancellableUntil
    ? isCancellationDeadlineExpired
      ? request.labels.notCancellable
      : formatDateTime(request.journey.cancellableUntil)
    : '';
  const itemTableRows = request.items
    .map(
      item => `<tr>
        <td style="vertical-align:top;padding:10px 6px 10px 0;">
          <strong>${escapeHtml(item.product)}</strong>
          <div style="margin-top:4px;font-size:11px;line-height:1.5;">
            <strong>${escapeHtml(request.labels.passenger)}:</strong> ${escapeHtml(item.passenger)}
          </div>
        </td>
        <td style="vertical-align:top;padding:10px 6px 10px 0;font-size:12px;line-height:1.45;">${escapeHtml(item.origin)}</td>
        <td style="vertical-align:top;padding:10px 6px 10px 0;font-size:12px;line-height:1.45;">${escapeHtml(item.destination)}</td>
        <td style="vertical-align:top;padding:10px 6px 10px 0;white-space:nowrap;">${formatVat(item.vatRate)}</td>
        <td style="vertical-align:top;padding:10px 0;white-space:nowrap;">${formatEuro(item.grossPrice)}</td>
      </tr>`
    )
    .join('');
  const hasJourneyDetails = Boolean(
    request.journey.operator ||
    request.journey.departure ||
    request.journey.arrival ||
    request.journey.changeableUntil ||
    request.journey.cancellableUntil ||
    existingTickets.length ||
    extras.length
  );
  const journeyDetailsRow = hasJourneyDetails
    ? `<tr>
      <td colspan="5" style="padding:10px 0 12px;border-top:1px solid #dddddd;font-size:11px;line-height:1.6;">
        ${request.journey.operator ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.operator)}:</strong> ${escapeHtml(request.journey.operator)}</span>` : ''}
        ${request.journey.departure ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.departure)}:</strong> ${escapeHtml(formatDateTime(request.journey.departure))}</span>` : ''}
        ${request.journey.arrival ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.arrival)}:</strong> ${escapeHtml(formatDateTime(request.journey.arrival))}</span>` : ''}
        ${request.journey.changeableUntil ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.changeableUntil)}:</strong> ${escapeHtml(formatDateTime(request.journey.changeableUntil))}</span>` : ''}
        ${request.journey.cancellableUntil ? (isCancellationDeadlineExpired ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.notCancellable)}</strong></span>` : `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.cancellableUntil)}:</strong> ${escapeHtml(cancellationText)}</span>`) : ''}
        ${existingTickets.length ? `<span style="display:inline-block;margin-right:14px;"><strong>${escapeHtml(request.labels.existingPublicTransportTickets)}:</strong> ${escapeHtml([...new Set(existingTickets)].join(', '))}</span>` : ''}
        ${extras.length ? `<span style="display:inline-block;"><strong>${escapeHtml(request.labels.extras)}:</strong> ${escapeHtml([...new Set(extras)].join(', '))}</span>` : ''}
      </td>
    </tr>`
    : '';
  const itemSummary = request.items
    .map(
      item =>
        `${item.product} – ${item.passenger}: ${formatEuro(item.grossPrice)}`
    )
    .join(', ');

  return {
    NAME: `${request.customer.firstName} ${request.customer.lastName}`,
    ORDER_ID: request.transactionId,
    CUSTOMER_NUMBER: request.customerReference,
    BOOKING_REFERENCE: request.bookingReference,
    BOOKING_DATETIME: formatDate(now.toISOString()),
    TOTAL_VAT: formatVat(0),
    TOTAL_PRICE: formatEuro(request.totalAmount),
    JOURNEY_DATE: journeyDate,
    ORIGIN: request.journey.origin,
    DESTINATION: request.journey.destination,
    DEPARTURE: formatDateTime(request.journey.departure),
    ARRIVAL: formatDateTime(request.journey.arrival),
    OPERATOR: request.journey.operator ?? '',
    CHANGEABLE_UNTIL: formatDateTime(request.journey.changeableUntil),
    CANCELLABLE_UNTIL: cancellationText,
    PASSENGERS: passengerSummary,
    PUBLIC_TRANSPORT_TICKETS: [
      ...request.publicTransportTickets,
      ...existingTickets,
    ].join(', '),
    EXTRAS: extras.join(', '),
    ITEMS: itemSummary,
    TABLE_CONTENT: itemTableRows + journeyDetailsRow,
    PHONE: request.customer.phone ?? '',
    WALLET: '',
    PAYMENT_SERVICE_PROVIDER_TEXT: '',
    CUSTOMER_NAME: '',
    CUSTOMER_ADDRESS: '',
    CUSTOMER_PHONE: '',
    CUSTOMER_SERVICE_PREFIX: '',
    CUSTOMER_SERVICE: '',
    FAREWELL_TEXT: '',
    BANNER_ALT: 'Banner Alt',
    HEADER_LOGO_ALT: 'Header Logo Alt',
  };
}

function formatVat(vatRate: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(vatRate / 100);
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: VIENNA_TIME_ZONE,
  }).format(date);
}

function formatDateTime(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: VIENNA_TIME_ZONE,
  }).format(date);
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, character => {
    const escapedCharacters: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return escapedCharacters[character];
  });
}
